import pako from 'pako';
import React, { useEffect, useContext } from 'react';

import { SOCKET_SUB_TYPE, SOCKET_TOPICS, TRADING_TYPE } from 'src/common/constants';
import { SOCKET_BASE_URL } from 'src/config';
import { useAppDispatch } from 'src/hooks';
import { updateUserTradeData as updateUserDerivativeTradeData } from 'src/setup/redux/slices/derivativesSlice';
import { updateTradeData } from 'src/setup/redux/slices/socketSlice';
import { updateUserTradeData as updateUserSpotTradeData } from 'src/setup/redux/slices/spotSlice';
import { getUserAssets } from 'src/setup/redux/slices/userSlice';
import { SubscribePayload } from 'src/types';

import SocketContext, { SubscriptionsData } from './socketContext';

const withWebSocket = (WrappedComponent: React.ComponentType<any>) => {
    const Wrapper = (props: any) => {
        const { payloads, tradeType } = props;
        const { addSocket, removeSocket, getSocket, updateSubscriptions, removeAllSockets } =
            useContext(SocketContext);
        const dispatch = useAppDispatch();

        useEffect(() => {
            const setMessageForTopic = (responseData: any, socketType: string) => {
                const { topic, data } = responseData;
                if (topic) {
                    switch (socketType) {
                        case SOCKET_SUB_TYPE.TRADE_DATA:
                            dispatch(updateTradeData({ responseData, tradeType }));
                            break;
                        case SOCKET_SUB_TYPE.USER:
                            if (tradeType === TRADING_TYPE.DERIVATIVES) {
                                dispatch(updateUserDerivativeTradeData({ data, topic }));
                            } else if (tradeType === TRADING_TYPE.SPOT) {
                                dispatch(updateUserSpotTradeData({ data, topic }));
                                if (topic === SOCKET_TOPICS.BALANCE) {
                                    dispatch(getUserAssets({ unit: 'USDT' }));
                                }
                            }
                            break;
                    }
                }
            };
            if (payloads && payloads.length === 0) return;
            payloads.forEach((payload: SubscribePayload) => {
                const { url, socketType, subscriptions } = payload;

                if (!getSocket(socketType)) {
                    const newSocket = new WebSocket(`${SOCKET_BASE_URL}${url}`);
                    let subscriptionsToSave: SubscriptionsData = {};
                    subscriptions.map((sub) => {
                        subscriptionsToSave[sub.id] = sub;
                    });

                    addSocket(socketType, newSocket, subscriptionsToSave);
                    newSocket.onopen = () => {
                        console.log(`WebSocket connection established to ${socketType}`);
                        subscriptions.forEach((msg) => newSocket.send(JSON.stringify(msg)));
                    };

                    newSocket.onmessage = (event) => {
                        let data = event.data;
                        if (event.data instanceof Blob) {
                            // Binary data
                            const reader = new FileReader();
                            reader.onload = function () {
                                if (reader.result instanceof ArrayBuffer) {
                                    data = new Uint8Array(reader.result);
                                    data = JSON.parse(pako.inflate(data, { to: 'string' }));
                                    setMessageForTopic(data, socketType);
                                }
                            };
                            reader.readAsArrayBuffer(event.data);
                        } else {
                            setMessageForTopic(JSON.parse(data), socketType);
                        }
                    };

                    newSocket.onclose = () => {
                        console.log(`WebSocket connection closed to ${socketType}`);
                        removeSocket(socketType);
                    };
                } else {
                    const socketObj = getSocket(socketType);
                    if (socketObj && socketObj.socket?.readyState === WebSocket.OPEN) {
                        const socket = socketObj.socket;
                        let subscriptionIds = new Set(subscriptions.map(({ id }) => id));
                        const subscriptionIdsToUpdate: SubscriptionsData = {};

                        for (const msg of Object.values(socketObj.subscriptions)) {
                            if (!subscriptionIds.has(msg.id)) {
                                msg.event = 'cancel';
                                socket.send(JSON.stringify(msg));
                            }
                        }
                        subscriptionIds = new Set(Object.keys(socketObj.subscriptions));
                        for (const msg of subscriptions) {
                            if (!subscriptionIds.has(msg.id)) {
                                socket.send(JSON.stringify(msg));
                            }
                            subscriptionIdsToUpdate[msg.id] = msg;
                        }
                        updateSubscriptions(socketType, subscriptionIdsToUpdate);
                    }
                }
            });
        }, [payloads]);

        useEffect(() => {
            return () => {
                removeAllSockets();
            };
        }, []);

        return <WrappedComponent {...props} />;
    };

    Wrapper.displayName = `withWebSocket(${WrappedComponent.displayName || WrappedComponent.name})`;
    return Wrapper;
};

export default withWebSocket;
